import { Button } from "@afterpaytouch/core";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import Link from "next/link";
import React, { useState } from "react";

import { KINDS, SIZES } from "@/components/buttons/buttons.constants";
import LinkButton from "@/components/buttons/link-button";

import { StoreProps } from "@/interfaces/stores-interface";

import ChevronUp from "../../assets/icons/chevron-up.svg";
import QRCode from "../../assets/images/app-download-qr-codes/US.svg";
import { ACCENT_COLORS } from "../component.constants";
import Grid from "../layout/grid";
import SplitterInfographics from "../splitters/splitter-infographics/splitter-infographics";
import BlurbDesc from "./blurb_desc.json";
import PhoneImage from "./phone_image.png";
import styles from "./retailerLandingPage.module.scss";

type Props = {
    store: StoreProps;
};

const getTheAppLink = "https://afterpayus.onelink.me/Ff2R/kvjyn0se";
export const RetailerLandingPage: React.FC<Props> = ({ store }) => {
    const { t } = useTranslation();

    const SEOHeader = () => {
        return (
            <Head>
                <title>{t("retailerLandingPage.seo.short", { merchantName: store.name })}</title>
                <meta
                    name="description"
                    content={t("retailerLandingPage.seo.long", { merchantName: store.name })}
                />
                <meta
                    property="og:description"
                    content={t("retailerLandingPage.seo.long", { merchantName: store.name })}
                />
                <meta
                    property="og:title"
                    content={t("retailerLandingPage.seo.short", { merchantName: store.name })}
                />
                <meta
                    property="twitter:title"
                    content={t("retailerLandingPage.seo.short", { merchantName: store.name })}
                />
                <meta
                    property="og:image"
                    content={"/ownit-assets/assets/afterpay/afterpay-share.jpeg"}
                />
                <meta
                    property="twitter:image"
                    content={"/ownit-assets/assets/afterpay/afterpay-share.jpeg"}
                />
                <meta property="og:type" content="website" />
                <meta property="twitter:card" content="summary_large_image" />
                <meta
                    property="og:site_name"
                    content={t("retailerLandingPage.seo.short", { merchantName: store.name })}
                />
                <link rel="canonical" href={`https://www.afterpay.com/stores/${store.slug}`} />
            </Head>
        );
    };

    const HeroSection = () => {
        return (
            <>
                <div className={styles.retailerPageLeftBar}>
                    <a href={getTheAppLink} className={styles.retailerLandingHeroImage}>
                        <img
                            src={store.unprocessedMainImageUrl || store.mainImageUrl}
                            alt={`${store.name} banner`}
                            className={styles.heroBanner}
                        />
                        <img
                            src={store.newLogoUrl}
                            alt={`${store.name} logo`}
                            className={styles.heroBannerLogo}
                        />
                    </a>
                </div>
                <div className={styles.retailerPageRightBar}>
                    <div className={styles.retailerLandingHeroContent}>
                        <div className={styles.heroContentTitle}>
                            {t("retailerLandingPage.hero.title", { merchantName: store.name })}
                        </div>
                        <p className={styles.heroContentTitleText}>
                            {t("retailerLandingPage.hero.subtitle", { merchantName: store.name })}
                        </p>
                        <div className={styles.heroContentAppContainer}>
                            <QRCode />
                            <div>
                                <LinkButton
                                    label={"Get the App"}
                                    kind={KINDS.NAVIGATION}
                                    size={SIZES.REGULAR}
                                    url={getTheAppLink}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const InfoSection = () => {
        return (
            <>
                <div
                    className={`${styles.retailerPageLeftBar} ${styles.retailerLandingInfoContainer}`}
                >
                    <div className={styles.retailerLandingInfoTitle}>
                        {t("retailerLandingPage.info.title", { merchantName: store.name })}
                    </div>
                    <ol className={styles.retailerLandingInfoBullets}>
                        <li>{t("retailerLandingPage.info.step1", { merchantName: store.name })}</li>
                        <li>{t("retailerLandingPage.info.step2", { merchantName: store.name })}</li>
                        <li>{t("retailerLandingPage.info.step3", { merchantName: store.name })}</li>
                    </ol>
                </div>
                <div className={styles.retailerPageRightBar}>
                    <div className={styles.retailerPageStoreTile}>
                        <img src={PhoneImage.src} />
                    </div>
                </div>
            </>
        );
    };

    const WhySection = (
        <SplitterInfographics
            title=""
            subtitle="Here's why you should Afterpay it"
            accentColor={ACCENT_COLORS.ORCA_PASTEL}
            fullWidth={false}
            sections={[
                {
                    headline: "Get what you need.",
                    body: `Shop ${store.name} and checkout with Afterpay.`,
                    infographic:
                        "https://cdn.builder.io/api/v1/image/assets%2Fb4f4a2d9d03a447491041054df7592d3%2F03f80f268f6f44779c64ce49f42ceba9",
                },
                {
                    headline: "Choose how you pay.",
                    body: "Pay in 4, generally over 6 weeks, or choose monthly plans over 3, 6, 12 or 24* months.",
                    infographic:
                        "https://cdn.builder.io/api/v1/image/assets%2Fb4f4a2d9d03a447491041054df7592d3%2F4de15f34a1ac4f59b5372d02969cd639",
                },
                {
                    headline: "No late fees when you pay on time for Pay in 4.",
                    body: "We'll send you reminders and cap late payments to help you get back on track.",
                    infographic:
                        "https://cdn.builder.io/api/v1/image/assets%2Fb4f4a2d9d03a447491041054df7592d3%2F19f1c586dc544f2198390ea6bfe5facf",
                },
            ]}
        />
    );

    const BnplSection = () => {
        return (
            <div className={styles.retailerLandingSection}>
                <div className={styles.retailerBnplTitle}>
                    {t("retailerLandingPage.bnpl-with.title", { merchantName: store.name })}
                </div>
                <div className={styles.retailerBnplSubtitle}>
                    {t("retailerLandingPage.bnpl-with.subtitle", { merchantName: store.name })}
                </div>
            </div>
        );
    };

    const [showDescription, setShowDescription] = useState(false);
    const AboutSection = () => {
        const blurb = BlurbDesc[store.slug]?.blurb || store.textBlurb;
        const desc = BlurbDesc[store.slug]?.description || store.description;
        return (
            <div className={styles.retailerLandingSection}>
                <div className={styles.retailerBnplTitle}>
                    {t("retailerLandingPage.about.title", { merchantName: store.name })}
                </div>
                <div className={styles.retailerLandingSectionContent}>{blurb}</div>
                {desc && (
                    <>
                        <div
                            className={styles.retailerLandingSectionContent}
                            style={{ display: showDescription ? "" : "none" }}
                        >
                            {desc}
                        </div>
                        <div className={styles.retailerLandingSectionContent}>
                            <LinkButton
                                onClick={() => setShowDescription(!showDescription)}
                                kind={KINDS.ICON}
                                size={SIZES.UNSET}
                                labelElement={
                                    <>
                                        <span>
                                            {showDescription
                                                ? t("retailerLandingPage.about.readLess")
                                                : t("retailerLandingPage.about.readMore")}
                                        </span>
                                    </>
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const LandingPageBreadcrumb = () => {
        return (
            <div className={styles.retailerLandingBreadcrumb}>
                <Link href={"/stores"}>Stores</Link>
                <div style={{ transform: "rotate(90deg)" }}>
                    <ChevronUp />
                </div>
                <div style={{ marginLeft: "20px" }}>{store.name}</div>
            </div>
        );
    };

    return (
        <div className={styles.retailerPageContainer}>
            <SEOHeader />
            <LandingPageBreadcrumb />
            <Grid fullWidth key={"hero"}>
                <HeroSection />
            </Grid>
            <Grid fullWidth key={"info"}>
                <InfoSection />
            </Grid>
            <div key={"why"}>{WhySection}</div>
            <div key={"bnpl"}>
                <BnplSection />
            </div>
            {(store.textBlurb || BlurbDesc[store.slug]) && (
                <div key={"about"}>
                    <AboutSection />
                </div>
            )}
        </div>
    );
};
